import { defineCustomElement } from 'vue'
import type { WebComponentConfig } from '../types/webComponents'

// Singleton-Klasse für Web-Komponenten-Management
class WebComponentManager {
  private static instance: WebComponentManager
  private components: Map<string, WebComponentConfig> = new Map()
  private isInitialized = false

  private constructor() {}

  public static getInstance(): WebComponentManager {
    if (!WebComponentManager.instance) {
      WebComponentManager.instance = new WebComponentManager()
    }
    return WebComponentManager.instance
  }

  /**
   * Registriert eine neue Web-Komponente
   */
  register(config: WebComponentConfig): this {
    if (this.components.has(config.tagName)) {
      console.warn(`Web-Komponente '${config.tagName}' ist bereits registriert`)
      return this
    }

    this.components.set(config.tagName, config)

    // Wenn bereits initialisiert, sofort definieren
    if (this.isInitialized) {
      this.defineComponent(config)
    }

    return this
  }

  /**
   * Registriert mehrere Web-Komponenten auf einmal
   */
  registerMultiple(configs: WebComponentConfig[]): this {
    configs.forEach((config) => this.register(config))
    return this
  }

  /**
   * Definiert eine einzelne Web-Komponente
   */
  private defineComponent(config: WebComponentConfig): void {
    const { component, tagName, options } = config

    if (!customElements.get(tagName)) {
      try {
        const customElement = defineCustomElement(component)
        customElements.define(tagName, customElement, options)
        console.log(`Web-Komponente '${tagName}' erfolgreich registriert`)
      } catch (error) {
        console.error(`Fehler beim Registrieren der Web-Komponente '${tagName}':`, error)
      }
    }
  }

  /**
   * Definiert alle registrierten Web-Komponenten
   */
  defineAll(): void {
    this.isInitialized = true

    this.components.forEach((config) => {
      this.defineComponent(config)
    })

    console.log(`${this.components.size} Web-Komponente(n) erfolgreich initialisiert`)
  }

  /**
   * Gibt alle registrierten Tag-Namen zurück
   */
  getRegisteredComponents(): string[] {
    return Array.from(this.components.keys())
  }

  /**
   * Prüft ob eine Komponente bereits registriert ist
   */
  isRegistered(tagName: string): boolean {
    return this.components.has(tagName)
  }

  /**
   * Entfernt eine registrierte Komponente (nur vor der Initialisierung)
   */
  unregister(tagName: string): boolean {
    if (this.isInitialized) {
      console.warn('Komponenten können nach der Initialisierung nicht mehr entfernt werden')
      return false
    }

    return this.components.delete(tagName)
  }

  /**
   * Setzt alle Registrierungen zurück (nur vor der Initialisierung)
   */
  reset(): boolean {
    if (this.isInitialized) {
      console.warn('Registry kann nach der Initialisierung nicht zurückgesetzt werden')
      return false
    }

    this.components.clear()
    return true
  }
}

// Singleton-Instanz exportieren
export const webComponentManager = WebComponentManager.getInstance()

// Convenience-Funktionen
export const registerWebComponent = (config: WebComponentConfig) =>
  webComponentManager.register(config)

export const registerWebComponents = (configs: WebComponentConfig[]) =>
  webComponentManager.registerMultiple(configs)

export const defineAllWebComponents = () => webComponentManager.defineAll()

export const getRegisteredWebComponents = () => webComponentManager.getRegisteredComponents()
